'use client'

import Button, { buttonClassNames } from '@components/primitives/Button'
import { Icon } from '@components/primitives/Icon'
import ModalOverlay from '@components/primitives/Modal/ModalOverlay'
import { typographyStyles } from '@components/primitives/Text/fontStyles'
import { classMerge } from '@components/utilities/classMerge'
import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons/faCheckCircle'
import { faFileCheck } from '@fortawesome/pro-solid-svg-icons/faFileCheck'
import { faFileExclamation } from '@fortawesome/pro-solid-svg-icons/faFileExclamation'
import { faFileInvoice } from '@fortawesome/pro-solid-svg-icons/faFileInvoice'
import { faSpinner } from '@fortawesome/pro-solid-svg-icons/faSpinner'
import { faXmark } from '@fortawesome/pro-solid-svg-icons/faXmark'
import { combineAudioStreamsToMediaRecorder } from '@lib/audio/combineAudioStreamsToMediaRecorder'
import PatientRoutes from '@lib/routes/PatientRoutes'
import { trackEvent } from '@lib/tracking'
import { TrackingEvents } from '@lib/tracking/TrackingEvents'
import { Call, Device } from '@twilio/voice-sdk'
import compact from 'lodash/compact'
import { useRouter } from 'next/navigation'
import { useCallback, useRef, useState } from 'react'
import { ClinicAgentAnimation } from './ClinicAgentAnimation'
import { PatientCallToSiteSkipModal } from './PatientCallToSiteSkipModal'

const consoleLogger = console

export function PatientCallToSiteInteraction({
  idToken,
  patientCallToSiteAccessToken,
  patientName,
  patientPhoneAlias,
  trialProRelayPhoneNumber,
  trialTitle,
  whyApplyStory,
}: {
  idToken: string
  patientCallToSiteAccessToken: string
  patientName?: string
  patientPhoneAlias: string
  trialProRelayPhoneNumber: string
  trialTitle: string
  whyApplyStory: string
}) {
  const router = useRouter()
  const [skipModalOpen, setSkipModalOpen] = useState(false)
  const [isCalling, setIsCalling] = useState(false)
  const deviceRef = useRef<Device | null>(null)
  const callRef = useRef<Call | null>(null)
  const mediaRecorderRef = useRef<MediaRecorder | undefined>(undefined)
  const advanceToNextStep = useCallback(() => {
    router.push(
      PatientRoutes.trialApplySurveyPostscreenerConsents(idToken, 'why-apply'),
    )
  }, [router, idToken])

  const setupDevice = useCallback(async () => {
    if (deviceRef.current) {
      return
    }

    const device = new Device(patientCallToSiteAccessToken, {
      // Set Opus as our preferred codec. Opus generally performs better, requiring less bandwidth and
      // providing better audio quality in restrained network conditions.
      codecPreferences: [Call.Codec.Opus, Call.Codec.PCMU],
      logLevel: 1,
    })
    device.on('registered', function () {
      consoleLogger.info('Twilio.Device Ready to make and receive calls!')
    })
    device.on('error', function (error) {
      consoleLogger.error('Twilio.Device Error: ' + error.message)
    })
    deviceRef.current = device
    return device
  }, [patientCallToSiteAccessToken])

  const setupPhoneCallWebClient = useCallback(async () => {
    const device = deviceRef.current ?? (await setupDevice())
    if (!device) {
      return
    }
    consoleLogger.info(
      'Setting up phone call web client with access token %o from %o to %o',
      patientCallToSiteAccessToken,
      patientPhoneAlias,
      trialProRelayPhoneNumber,
    )
    trackEvent(TrackingEvents.PATIENT_CALL_TO_SITE_CALL_DIALED, {
      patientPhoneAlias,
      trialApplicationIdToken: idToken,
      trialProRelayPhoneNumber,
    })

    const params = {
      CallerId: patientPhoneAlias,
      To: trialProRelayPhoneNumber,
      idToken,
    }

    if (callRef.current) {
      callRef.current.disconnect()
      callRef.current = null
    }

    const call = await device.connect({ params }) // performs the actual dial
    callRef.current = call

    call.on('accept', function (call: Call) {
      consoleLogger.info('Twilio.Call accepted' + JSON.stringify(call))
      if (call.getLocalStream() && call.getRemoteStream()) {
        mediaRecorderRef.current = combineAudioStreamsToMediaRecorder(
          compact([call.getLocalStream(), call.getRemoteStream()]),
        )
      }
    })
    call.on('reject', function (call: Call) {
      consoleLogger.info('Twilio.Call rejected' + JSON.stringify(call))
    })
    call.on('disconnect', function (call: Call) {
      consoleLogger.info('Twilio.Call disconnected' + JSON.stringify(call))
      trackEvent(TrackingEvents.PATIENT_CALL_TO_SITE_CALL_END_CLICKED, {
        trialApplicationIdToken: idToken,
        val: 'Call disconnected',
      })

      consoleLogger.info('advanceToNextStep')
      advanceToNextStep()
    })
  }, [advanceToNextStep])

  const endCall = useCallback(() => {
    if (callRef.current) {
      callRef.current.disconnect()
      callRef.current = null
    }
    advanceToNextStep()
  }, [callRef.current])

  return (
    <ModalOverlay
      className='bg-gradient-to-b from-white via-white to-primary100'
      onClick={() => {}}
    >
      <PatientCallToSiteSkipModal
        isOpen={skipModalOpen}
        onSkip={() => {
          trackEvent(TrackingEvents.PATIENT_CALL_TO_SITE_CALL_END_CLICKED, {
            trialApplicationIdToken: idToken,
            val: 'Skip button clicked',
          })
          endCall()
        }}
        setIsOpen={setSkipModalOpen}
        whyApplyStory={whyApplyStory}
      />
      <Button.Icon
        className='fixed right-4 top-4 z-navbarContainer flex size-8 items-center justify-center rounded-full bg-neutral200 text-neutral600 hover:bg-neutral300 hover:text-neutral800 md:right-32'
        icon={faXmark}
        onClick={() => {
          trackEvent(TrackingEvents.PATIENT_CALL_TO_SITE_SKIP_BUTTON_CLICKED, {
            trialApplicationIdToken: idToken,
            type: 'x button',
          })
          setSkipModalOpen(true)
        }}
        type='button'
      />

      <div className='mx-4 flex h-full max-w-5xl flex-col gap-8 py-16 md:mx-auto md:pt-32'>
        <div className='flex h-full flex-col justify-between gap-4 md:flex-row md:items-start'>
          <div className='flex flex-col gap-4 md:w-2/3'>
            <ClinicAgentAnimation
              clinicName='Power'
              mediaRecorder={mediaRecorderRef.current}
              patientName={patientName}
            />
            <h2 className={classMerge(typographyStyles['h2'], 'text-black')}>
              We&apos;re connecting you to the clinic, please stay on the line
            </h2>
            <h6
              className={classMerge(typographyStyles['h6'], 'text-neutral900')}
            >
              WHAT TO SAY WHEN THE CLINIC PICKS UP
            </h6>
            <p
              className={classMerge(
                typographyStyles['p-strong'],
                'text-primary600',
              )}
            >
              Hi, I just submitted an application on Power for the {trialTitle}{' '}
              study. I&apos;d like to learn more and discuss next steps.
            </p>

            <Button
              className={classMerge(
                buttonClassNames.primary.black,
                isCalling && 'hidden',
                'mx-auto w-full justify-center rounded-full md:w-1/2',
              )}
              onClick={() => {
                trackEvent(TrackingEvents.PATIENT_CALL_TO_SITE_CALL_STARTED, {
                  trialApplicationIdToken: idToken,
                })
                setIsCalling(true)
                setupPhoneCallWebClient()
              }}
            >
              Start Call &rarr;
            </Button>

            <Button
              className={classMerge(
                buttonClassNames.primary.black,
                !isCalling && 'hidden',
                'mx-auto w-full justify-center rounded-full md:w-1/2',
              )}
              onClick={() => {
                trackEvent(
                  TrackingEvents.PATIENT_CALL_TO_SITE_CALL_END_CLICKED,
                  {
                    trialApplicationIdToken: idToken,
                    val: 'End call button clicked',
                  },
                )
                endCall()
              }}
            >
              End Call
            </Button>
          </div>

          <ApplicationCard patientName={patientName} />
        </div>
      </div>
    </ModalOverlay>
  )
}

function ApplicationCard({ patientName }: { patientName?: string }) {
  return (
    <div className='fixed inset-x-0 bottom-0 m-4 flex flex-col items-center justify-center overflow-hidden rounded-2xl bg-white md:static md:w-1/3 md:drop-shadow-lg'>
      <div className='relative h-5 w-full'>
        <div className='absolute size-full bg-primary100'></div>
        <div className='absolute size-full w-[75%] bg-primary600'></div>
      </div>
      <div className='flex flex-col gap-4 p-4'>
        <div className='flex items-center gap-2'>
          <div className='flex w-2/3 items-center gap-3 rounded-md bg-primary50 px-4 py-2 text-sm font-medium text-primary600'>
            <Icon className='text-primary600' icon={faFileInvoice} size='2x' />{' '}
            {patientName ? `${patientName}'s` : 'Your'} Application
          </div>
          <div className='flex w-1/3 flex-col items-end gap-2'>
            <div className='flex items-center justify-end gap-2 text-xs'>
              75% done <Icon className='text-primary600' icon={faFileCheck} />
            </div>
            <div className='flex items-center justify-end gap-2 text-xs'>
              3 items left{' '}
              <Icon className='text-primary100' icon={faFileExclamation} />
            </div>
          </div>
        </div>
        <div className='hidden w-full items-center justify-between gap-2 px-3 text-sm font-semibold md:flex'>
          Personal info
          <div className='flex items-center gap-2 text-primary600'>
            Verified
            <Icon className='text-primary600' icon={faCheckCircle} />
          </div>
        </div>
        <div className='hidden w-full items-center justify-between gap-2 px-3 text-sm font-semibold md:flex'>
          Conditions
          <div className='flex items-center gap-2 text-primary600'>
            Verified
            <Icon className='text-primary600' icon={faCheckCircle} />
          </div>
        </div>
        <div className='hidden w-full items-center justify-between gap-2 px-3 text-sm font-semibold md:flex'>
          Medications
          <div className='flex items-center gap-2 text-primary600'>
            Verified
            <Icon className='text-primary600' icon={faCheckCircle} />
          </div>
        </div>
        <div className='hidden w-full items-center justify-between gap-2 rounded-lg bg-primary50 px-3 py-2 text-sm font-semibold text-primary600 md:flex'>
          Screening Call
          <div className='flex items-center gap-4 text-primary600'>
            In-progress
            <Icon className='text-primary600' icon={faSpinner} />
          </div>
        </div>
      </div>
    </div>
  )
}
